import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Menu, MapPin, Dumbbell, Heart, User, ChevronDown, Newspaper } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { Gym } from '../types';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import HeaderSearch from './HeaderSearch';
import { MenuButton } from '../src/components/ui/menu-button';
import { useFocusTrap } from '../src/hooks/useFocusTrap';
import { STATE_NAV_GROUPS } from '../lib/stateRoutes';
import { matchesBreakpointUp } from '../lib/breakpoints';

interface HeaderProps {
    onGymSelect: (gym: Gym) => void;
    isScrolled?: boolean;
    variant?: 'transparent' | 'solid';
}

/**
 * ⚠️ `onViewMap` / `showViewMap` WERE PROPS HERE AND ARE GONE (operator-ruled 2026-08-16).
 * They existed solely to render the "Search Gyms" shortcut inside the old combined "Gyms"
 * dropdown; the per-state menus that replaced it hold only each state's two destinations.
 * Removed rather than left dangling, because `noUnusedLocals` would have failed the build
 * on the dead bindings and silencing that would have hidden genuinely dead props.
 *
 * The other `onViewMap` props in App.tsx belong to DIFFERENT components (GymsSegmentRoute,
 * GymProfileRoute) and are untouched — the name is shared, the prop is not.
 */

/**
 * Responsive tiers:
 *  - lg (≥1024px): full inline layout — logo · centred search · Blog/Suburbs/Search Gyms · account.
 *  - md (768–1023px): logo · inline search · hamburger (nav + account live in the drawer).
 *  - <md: scaled logo · search-icon (expands the same autocomplete overlay) · hamburger.
 * Primary nav and search are reachable at every breakpoint.
 */
const Header: React.FC<HeaderProps> = ({
    onGymSelect,
    isScrolled = false,
    variant = 'solid'
}) => {
    const location = useLocation();
    const isBlogActive = location.pathname === '/blog';
    /**
     * ⭐ WHICH STATE'S MENU READS ACTIVE (operator-ruled 2026-08-16). Replaces the pair
     * of flags the single "Gyms" trigger used, because with a menu PER STATE the question
     * changed from "is any directory surface active" to "WHICH state am I in".
     *
     * Matched on the path's leading segment rather than on the two menu destinations, so
     * the state stays lit everywhere inside it — a suburb page, a style page, a gym
     * profile — not only on the two addresses the menu links to. That is the UI-09 defect
     * in its newest form: the old trigger read INACTIVE on the very directory roots it
     * pointed at, and a per-state menu that only lit on its own two links would repeat it
     * one level down.
     *
     * Derived from STATE_NAV_GROUPS, so a third state is matched with no edit here.
     */
    const activeStateSlug: string | null = (() => {
        const first = location.pathname.split('/')[1] ?? '';
        return STATE_NAV_GROUPS.some((g) => g.slug === first) ? first : null;
    })();
    const { user, userProfile, isLoading, isAuthModalOpen, closeAuthModal } = useAuth();
    // NOTE (UI-46): the lg account and "Gyms" dropdowns own their open state inside
    // MenuButton. Header used to MIRROR it (isMenuOpen / isGymsMenuOpen) for one
    // reason only — keeping the auto-hiding header revealed while a menu was open.
    // With the auto-hide removed, nothing reads those values, so the mirrors and
    // their onOpenChange wiring are gone rather than left as dead state.
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);   // mobile/tablet nav drawer
    const [isSearchOpen, setIsSearchOpen] = useState(false);   // mobile search overlay
    // UI-46 (operator-ruled 2026-08-08): the desktop AUTO-HIDE IS REMOVED. The header
    // is now a plain fixed bar on every route at every breakpoint, and every page
    // offsets below it. What that deleted, recorded so nobody reintroduces it
    // piecemeal: the isCollapsible prop, the isHovered/isFocusWithin/isRevealed
    // state trio, the post-hydration reveal effect, the 16px hover sensor strip,
    // the "Show navigation" skip button, `inert` on the nav, the translateY
    // transform, and the nav's mouse/focus handlers. UI-04's guarantee (the header
    // must never be DELIVERED hidden) is now structural rather than maintained —
    // there is no hidden state left to deliver.
    // Defer auth-dependent rendering until after hydration to prevent SSG mismatch
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => { setHasMounted(true); }, []);
    // Auth is "resolved" only once we've hydrated AND Firebase has restored (or cleared)
    // the session from its async store. Until then the account slot renders a neutral
    // placeholder instead of asserting "Sign-in" — this is what removes the
    // signed-out→signed-in flash a returning user saw on every load.
    const authResolved = hasMounted && !isLoading;
    const searchPanelRef = useRef<HTMLDivElement>(null);
    const searchToggleRef = useRef<HTMLButtonElement>(null);
    const hamburgerRef = useRef<HTMLButtonElement>(null);
    const drawerCloseRef = useRef<HTMLButtonElement>(null);
    const drawerRef = useRef<HTMLDivElement>(null);

    // Close the account dropdown / mobile search overlay on click outside.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                searchPanelRef.current && !searchPanelRef.current.contains(target) &&
                searchToggleRef.current && !searchToggleRef.current.contains(target)
            ) {
                setIsSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        hamburgerRef.current?.focus();
    };

    // Close the mobile search overlay on Escape. The drawer's own Escape is handled
    // by useFocusTrap's onEscape (closeDrawer below), which ALSO restores focus to the
    // hamburger — matching the ✕ button, so keyboard focus is never stranded on <body>.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsSearchOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Modal drawer focus management (initial focus, body-scroll lock, Tab/Shift+Tab
    // trap, Escape) via the shared hook. restoreFocus is off because onEscape → closeDrawer
    // returns focus to the hamburger explicitly, covering BOTH the ✕ button and Escape.
    useFocusTrap({
        active: isDrawerOpen,
        containerRef: drawerRef,
        initialFocusRef: drawerCloseRef,
        lockScroll: true,
        restoreFocus: false,
        onEscape: closeDrawer,
    });

    // Collapse the drawer / search overlay when the viewport grows past their tier,
    // so a locked body / stale state can't survive a resize.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => {
            // Item 1: asks the browser the SAME rem question the stylesheet asks.
            // These two tiers must agree with the classes that show/hide the controls
            // they close — the drawer's trigger is `lg:hidden`, the search's is
            // `md:hidden`. When this counted PIXELS (>= 1024 / >= 768) and the
            // stylesheet switched at 64rem / 48rem, a visitor with a raised default
            // font size hit a band where the hamburger was still the only menu and
            // this handler closed the drawer anyway — no menu bar, no drawer, no
            // navigation. See lib/breakpoints.ts.
            if (matchesBreakpointUp('lg')) setIsDrawerOpen(false);
            if (matchesBreakpointUp('md')) setIsSearchOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Two visual states: transparent at top, solid dark when scrolled
    const bgClass = variant === 'transparent' && !isScrolled
        ? 'bg-transparent'
        : 'bg-surface-ink';

    // Consistent keyboard focus ring for the chrome's raw controls (they otherwise
    // only style :hover, leaving keyboard focus barely visible on the dark bar). Inset
    // so it renders cleanly on both the solid and the transparent (landing-top) header
    // without needing a matching ring-offset colour.
    const focusRing =
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-on-dark focus-visible:ring-inset';

    // Shared classes for the drawer rows.
    const drawerRow = (active: boolean) =>
        `flex w-full items-center gap-3 px-menu-row-x py-menu-row-y text-base font-medium transition-colors text-left ${focusRing} ${
            active ? 'text-white bg-white/5' : 'text-text-subtle hover:text-white hover:bg-white/5'
        }`;

    /* H6 / UI-34 — THE HEADER ICON SCALE (operator-ruled 2026-08-07: document, keep sizes).
       Four glyph sizes are in deliberate use; pick from this scale, don't invent a fifth:
         14 — small companions to SMALL text: the dropdown trigger chevrons, and the
              Dumbbell/User glyphs in the drawer's uppercase SECTION HEADERS (Gyms /
              Account — text-xs labels, so their glyphs shrink with them);
         18 — full-size row/label icons: the row-leading icons in drawer and dropdown
              rows (Newspaper, MapPin, Search, User, Heart) AND the Dumbbell/User
              glyphs inside the desktop dropdown TRIGGER labels (text-base context);
         20 — the mobile search toggle's swap pair (Search/X) in the 44px h-11 button;
         22 — the top-bar menu controls (hamburger Menu, drawer close X) in their 44px
              buttons — 2px larger than the search pair so the primary door reads heavier.
       The 44px TAP TARGETS are separate (H4, h-11 w-11) — this scale governs glyphs only. */
    return (
        <>
            {/* UI-50 — skip link. MUST be the first focusable element in the document,
                which is why it precedes the <nav> rather than living inside it.
                WCAG 2.4.1: before this, a keyboard or screen-reader user tabbed the
                whole bar (~3 controls on a phone, ~5 on desktop) on every one of the
                4,221 prerendered pages, on every visit.

                Plain, CSS-hidden markup ON PURPOSE: conditionally-rendered branches do
                NOT survive prerendering, but CSS-hidden markup DOES — so this ships on
                every page rather than appearing only after hydration.

                COVERS: every page, because `id="main-content"` is on the single main
                landmark of all 16 page components plus App's invalid-gym guard.
                DOES NOT COVER: nothing currently. If a new page type is added without
                that id, this link silently does nothing there — there is no build-time
                assertion tying the two together (see the register's UI-27 build-chain
                item, which would be the natural home for one).

                z-[80] clears the header's own z-50 nav and its z-[60]/z-[70] overlays;
                it sits far below FilterModal's z-[1100] band, which is correct — a skip
                link must not paint over an open dialog. */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[80] focus:rounded-md focus:bg-surface-base focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-text-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
                Skip to main content
            </a>
            <nav
                aria-label="Primary navigation"
                // UI-46 + item 6 (2026-08-14): the height floor is the SHARED
                // `header` token (4rem, tailwind.config.js), and every page offsets by
                // the same token (top-header / pt-header / h-header) — so bar and
                // offsets cannot drift apart, because there is only one number.
                // It was `min-h-[64px]` — pixels — which meant a non-16px root font
                // shrank the offsets while the bar held 64px, re-opening the very
                // overlap this item closed. The token must stay in REM for that
                // reason; `lib/headerOffset.test.ts` fails if it becomes px.
                className={`fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 py-2.5 md:px-12 min-h-header w-full ${bgClass} text-white`}
            >
                {/* Logo — Link gives proper anchor semantics (middle-click new tab,
                    right-click open in new tab, hover URL on focus). Scaled down on
                    phones to reclaim horizontal space for the compact controls. */}
                <Link
                    to="/"
                    className={`flex items-center font-semibold text-lg md:text-xl tracking-tight cursor-pointer flex-shrink-0 ${focusRing}`}
                >
                    <span>Martial Arts Network</span>
                </Link>

                {/* Inline search. Shown from md up; below md it lives in the search overlay.
                    At md it's an in-flow flex item (grows to fill the space between the logo
                    and the hamburger). From lg up it's absolutely viewport-centred for true
                    symmetry; through the lg band it's narrowed via calc so the centred bar
                    still clears the full inline nav, then settles at its natural 28rem from
                    xl up. The 848px term reserves padding + logo + nav on both sides (sized
                    for the wider logged-in nav, with margin). */}
                <div className="hidden md:block flex-1 min-w-0 px-4 lg:absolute lg:left-1/2 lg:top-1/2 lg:w-[calc(100vw-848px)] lg:max-w-[28rem] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:px-0 xl:w-[28rem]">
                    <HeaderSearch onGymSelect={onGymSelect} className="max-w-md mx-auto" />
                </div>

                {/* Right Navigation */}
                <div className="flex items-center gap-2 lg:gap-6 text-base font-medium flex-shrink-0">
                    {/* ⭐ ONE MENU PER LAUNCHED STATE — "NSW" and "VIC" (operator-ruled
                        2026-08-16), replacing the single combined "Gyms" dropdown. Each holds
                        that state's two destinations: its directory and its suburb A–Z.
                        Below lg these live in the drawer.

                        ⚠️ DERIVED FROM STATE_NAV_GROUPS, NOT HAND-WRITTEN, AND THAT IS THE
                        WHOLE POINT. Two literal menus would satisfy the instruction and
                        silently delete UI-09's property — that launching a third state adds
                        it to every chrome surface with no component edit. Mapping keeps both.
                        The "Search Gyms" entry that lived here is GONE by the same ruling: it
                        only ever appeared on pages that already show a search and filter
                        control, so nothing became unreachable. */}
                    {/* ⭐ NO ROW ICONS, AND w-52 → w-40 (operator-ruled 2026-08-17) — one change,
                        not two: 208px was sized for icon + gap-3 + label, so removing the MapPin
                        without narrowing the panel would leave the rows looking like an icon had
                        gone missing. 160px hugs "NSW Suburbs", the longest of the four labels.
                        This completes UI-94, which took the icon off the desktop TRIGGER and left
                        the dropdown ROWS still carrying one.

                        ⚠️ DESKTOP ONLY, AND THAT IS THE RULING RATHER THAN AN OVERSIGHT. The
                        drawer renders these same STATE_NAV_GROUPS links in a SEPARATE block below
                        and KEEPS its MapPin — glyphs carry scanning weight in a vertical list.
                        Tablet (768) and phone (375) use the drawer, so neither is touched by this.
                        Do not "restore consistency" in either direction.

                        ⚠️ The ACCOUNT menu's rows also keep their icons, by the same 2026-08-17
                        ruling. This change was scoped to the four state links the operator named
                        — it is not a general "no icons in dropdowns" rule. */}
                    <div className="hidden lg:flex items-center gap-6">
                        {STATE_NAV_GROUPS.map((group) => (
                            <MenuButton
                                key={group.slug}
                                menuLabel={group.short}
                                align="start"
                                panelClassName="w-40"
                                items={group.links.map((link) => ({
                                    id: link.id,
                                    to: link.to,
                                    label: link.label,
                                }))}
                                trigger={({ open, ref, triggerProps }) => (
                                    <button
                                        type="button"
                                        ref={ref}
                                        {...triggerProps}
                                        className={`flex items-center gap-1.5 font-medium text-base transition-colors ${focusRing} ${
                                            activeStateSlug === group.slug ? 'text-white' : 'text-text-subtle hover:text-white'
                                        }`}
                                    >
                                        <span>{group.short}</span>
                                        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            />
                        ))}
                    </div>

                    {/* ⭐ BLOG SITS AFTER THE STATE MENUS (operator-ruled 2026-08-17): the
                        desktop order is NSW · VIC · Blog · Account. The drawer below carries
                        the SAME order — ⚠️ it is a separate block, so an lg-only edit would
                        leave the phone and tablet menu on the old order, which is the exact
                        half a desktop review cannot see.

                        ⚠️ NO ICON HERE, AND THAT IS THE RULING RATHER THAN AN OMISSION.
                        Blog is the one nav item that never had one; asked to resolve the
                        mismatch, the operator ruled the desktop row goes uniformly TEXT-ONLY
                        (2026-08-17) — so the state menus' Dumbbell and the account menu's
                        User were REMOVED above and below rather than a Newspaper added here.
                        ⚠️ The DRAWER keeps its icons deliberately: they aid scanning in a
                        vertical list, which the horizontal row does not need. Do not
                        "restore consistency" by copying the drawer's icons up here. */}
                    <Link
                        to="/blog"
                        className={`hidden lg:inline-flex font-medium text-base transition-colors ${focusRing} ${
                            isBlogActive ? 'text-white' : 'text-text-subtle hover:text-white'
                        }`}
                    >
                        Blog
                    </Link>

                    {/* Account — inline at lg+, in the drawer below lg. A neutral
                        placeholder shows until auth resolves, so a returning user never
                        sees the 'Sign-in' link flip to their account menu. */}
                    {!authResolved ? (
                        <div className="hidden lg:block h-6 w-[4.5rem] rounded-md bg-white/10 motion-safe:animate-pulse" aria-hidden="true" />
                    ) : user ? (
                        <div className="hidden lg:block">
                            <MenuButton
                                menuLabel="Account menu"
                                align="end"
                                panelClassName="w-48"
                                items={[
                                    {
                                        id: 'account',
                                        to: '/account',
                                        label: user.email?.split('@')[0] || 'Account',
                                        icon: userProfile?.photoURL
                                            ? <img src={userProfile.photoURL} alt="" className="w-5 h-5 rounded-full object-cover" />
                                            : <User size={18} className="text-text-subtle" />,
                                    },
                                    { type: 'divider' as const },
                                    { id: 'favourites', to: '/favourites', label: 'Favourites', icon: <Heart size={18} className="text-text-subtle" /> },
                                ]}
                                trigger={({ open, ref, triggerProps }) => (
                                    <button
                                        ref={ref}
                                        {...triggerProps}
                                        className={`flex items-center gap-1.5 text-text-subtle hover:text-white transition-colors ${focusRing}`}
                                    >
                                        {/* No icon — the desktop row is text-only by the
                                            2026-08-17 ruling. The account MENU ITEMS keep
                                            their icons; only the TRIGGER is bare. */}
                                        <span>Account</span>
                                        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            />
                        </div>
                    ) : (
                        <Link
                            to="/auth"
                            className={`hidden lg:block text-base font-medium text-brand-primary-on-dark hover:text-white transition-colors p-0 bg-transparent shrink-0 ${focusRing}`}
                        >
                            Sign-in
                        </Link>
                    )}

                    {/* Search toggle — below md only (md+ shows the inline bar) */}
                    <button
                        ref={searchToggleRef}
                        type="button"
                        onClick={() => setIsSearchOpen(open => !open)}
                        aria-label="Search gyms"
                        aria-expanded={isSearchOpen}
                        className={`md:hidden flex items-center justify-center h-11 w-11 rounded-full text-text-subtle hover:text-white hover:bg-white/10 transition-colors ${focusRing}`}
                    >
                        {isSearchOpen ? <X size={20} /> : <Search size={20} />}
                    </button>

                    {/* Hamburger — below lg only */}
                    <button
                        ref={hamburgerRef}
                        type="button"
                        onClick={() => setIsDrawerOpen(true)}
                        aria-label="Open menu"
                        aria-haspopup="dialog"
                        aria-expanded={isDrawerOpen}
                        className={`lg:hidden flex items-center justify-center h-11 w-11 rounded-full text-text-subtle hover:text-white hover:bg-white/10 transition-colors ${focusRing}`}
                    >
                        <Menu size={22} />
                    </button>
                </div>

                {/* Mobile search overlay — the SAME autocomplete as the inline bar */}
                {isSearchOpen && (
                    <div
                        ref={searchPanelRef}
                        className="md:hidden absolute top-full left-0 right-0 px-6 pb-4 pt-1 bg-surface-ink border-t border-white/10 shadow-elevation-2 z-50"
                    >
                        <HeaderSearch
                            onGymSelect={onGymSelect}
                            onAfterSelect={() => setIsSearchOpen(false)}
                            autoFocus
                            className="w-full"
                        />
                    </div>
                )}
            </nav>

            {/* Mobile / tablet navigation drawer (below lg) */}
            {isDrawerOpen && (
                <div className="lg:hidden">
                    <div
                        className="fixed inset-0 z-[60] bg-black/50 animate-backdrop-in"
                        aria-hidden="true"
                        onClick={closeDrawer}
                    />
                    <div
                        ref={drawerRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Menu"
                        className="fixed top-0 right-0 z-[70] flex h-full w-72 max-w-[80vw] flex-col bg-surface-ink text-white shadow-elevation-2 animate-drawer-in"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                            <span className="font-semibold text-lg">Menu</span>
                            <button
                                ref={drawerCloseRef}
                                type="button"
                                onClick={closeDrawer}
                                aria-label="Close menu"
                                className={`flex items-center justify-center h-11 w-11 rounded-full text-text-subtle hover:text-white hover:bg-white/10 transition-colors ${focusRing}`}
                            >
                                <X size={22} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto py-2">
                            {/* ⭐ ONE DRAWER SECTION PER STATE — the mobile half of the same
                                2026-08-16 ruling. The desktop menus and these sections render
                                from the SAME STATE_NAV_GROUPS, so they cannot drift: a change
                                to either surface's links is a change to both.

                                ⚠️ This half is the one an operator reviewing on desktop never
                                sees. A nav change applied only to the lg+ layout would look
                                correct on web and leave every phone on the old menu. */}
                            {STATE_NAV_GROUPS.map((group) => (
                                <React.Fragment key={group.slug}>
                                    <p className="flex items-center gap-2 px-5 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
                                        <Dumbbell size={14} aria-hidden="true" />
                                        {group.short}
                                    </p>
                                    {group.links.map((link) => (
                                        <Link
                                            key={link.id}
                                            to={link.to}
                                            onClick={closeDrawer}
                                            className={drawerRow(location.pathname === link.to)}
                                        >
                                            <MapPin size={18} />
                                            <span>{link.label}</span>
                                        </Link>
                                    ))}
                                </React.Fragment>
                            ))}

                            {/* ⭐ BLOG AFTER THE STATE SECTIONS — the drawer half of the
                                2026-08-17 order ruling (NSW · VIC · Blog · Account), kept in
                                step with the desktop row above so the two cannot drift.
                                ⚠️ THE ICON STAYS HERE. The same ruling stripped icons from the
                                DESKTOP row only; in a vertical list the glyphs carry scanning
                                weight, and the drawer's state and account rows keep theirs
                                too. Bare desktop + iconed drawer is deliberate, not a miss. */}
                            <Link to="/blog" onClick={closeDrawer} className={drawerRow(isBlogActive)}>
                                <Newspaper size={18} />
                                <span>Blog</span>
                            </Link>

                            {!authResolved ? (
                                <div className="px-5 pt-4 pb-2" aria-hidden="true">
                                    <div className="h-4 w-24 rounded bg-white/10 motion-safe:animate-pulse" />
                                </div>
                            ) : user ? (
                                <>
                                    {/* Account — grouped section header (parallel to GYMS), shown only
                                        when signed in, where it caps two real items. Signed out there's
                                        just a single Sign-in link, so a header would be over-heavy. */}
                                    <p className="flex items-center gap-2 px-5 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
                                        <User size={14} aria-hidden="true" />
                                        Account
                                    </p>
                                    <Link to="/account" onClick={closeDrawer} className={drawerRow(false)}>
                                        {userProfile?.photoURL ? (
                                            <img src={userProfile.photoURL} alt="" className="w-5 h-5 rounded-full object-cover" />
                                        ) : (
                                            <User size={18} />
                                        )}
                                        <span className="truncate">{user.email?.split('@')[0] || 'Account'}</span>
                                    </Link>
                                    <Link to="/favourites" onClick={closeDrawer} className={drawerRow(false)}>
                                        <Heart size={18} />
                                        <span>Favourites</span>
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <div className="my-2 border-t border-white/10" />
                                    <Link
                                        to="/auth"
                                        onClick={closeDrawer}
                                        className={`block w-full text-left px-menu-row-x py-menu-row-y text-base font-medium text-brand-primary-on-dark hover:text-white transition-colors ${focusRing}`}
                                    >
                                        Sign-in
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Auth Modal */}
            <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
        </>
    );
};

export default React.memo(Header);
